'use client';

import { useState } from "react";
import classes from "./public_provider_contact.module.css";
import ChevronDown from '@/assets/svg/icons/chevron-down.svg';
import ChevronUp from '@/assets/svg/icons/chevron-up.svg';
import Send from '@/assets/svg/icons/send.svg';
import PhoneCall from '@/assets/svg/icons/phone-call.svg';
import CustomSVG from "../common/svg";
import Avatar from "../common/avatar/avatar";
import CustomButton from "../common/buttons/button";
import { phoneClick } from "@/utils/http";
import { useMutation } from "@tanstack/react-query";
import { usePublicProviderInteraction } from "./public_provider_interaction_context";
import useAnalyticsTracking from "../common/analytics/use_analytics_tracking";

export default function PublicProviderContact({provider, setContactMobile: setContactMobileProp}) {
    const [isContactOpen, setIsContactOpen] = useState(false);
    const [isMapOpen, setIsMapOpen] = useState(false);
    const { openContactMobile } = usePublicProviderInteraction();
    const openContact = setContactMobileProp ? () => setContactMobileProp(true) : openContactMobile;

    const toggleContact = () => { setIsContactOpen(!isContactOpen); };
    const toggleMap = () => { setIsMapOpen(!isMapOpen); };

    if(!provider.contact) return null;

    const googleMapsApiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;

    const hasCoords = provider.geo?.lat && provider.geo?.lng;
    const hasAddress = provider.address && (provider.address.city || provider.address.street);
    const mapQuery = hasCoords
        ? `${provider.geo.lat},${provider.geo.lng}`
        : [provider.address?.zip, provider.address?.city, provider.address?.street].filter(Boolean).join(' ');

    return <div className={`${classes["public-provider-contact-main"]}`}>
        {/* Kapcsolat blokk */}
        <div className="d-flex flex-column align-self-stretch gap-2">
            <div className="d-flex flex-row align-items-center justify-content-between w-100">
                <h3 className="margin-0 color-gray-800 text-xl-medium">Kapcsolat</h3>
                <CustomSVG 
                    className="flex-shrink-0 stroke-gray-800 pointer" 
                    SvgFunction={isContactOpen ? ChevronUp : ChevronDown} 
                    height={24} 
                    width={24} 
                    onClick={toggleContact} 
                />
            </div>
            {isContactOpen && (
                <PublicProviderProfileContact 
                    id={provider.id} 
                    image={provider.contact.image} 
                    name={provider.contact.name} 
                    providerName={provider.name}
                    email={provider.contact.email} 
                    phone={provider.contact.phone} 
                    extraText={provider.contact.extra_text}
                    openContact={openContact}
                />
            )}
        </div>

        {/* Térkép blokk */}
        {(hasCoords || hasAddress) && (
            <div className="d-flex flex-column align-self-stretch gap-2">
                <div className="d-flex flex-row align-items-center justify-content-between w-100">
                    <h3 className="margin-0 color-gray-800 text-xl-medium">Térkép</h3>
                    <CustomSVG 
                        className="flex-shrink-0 stroke-gray-800 pointer" 
                        SvgFunction={isMapOpen ? ChevronUp : ChevronDown} 
                        height={24} 
                        width={24} 
                        onClick={toggleMap} 
                    />
                </div>
                {isMapOpen && (
                    <div className={`${classes["public-provider-contact-section"]}`}>
                        <div className={`${classes["public-provider-contact-map-container"]}`}>
                            <iframe
                                src={`https://www.google.com/maps/embed/v1/place?key=${googleMapsApiKey}&q=${encodeURIComponent(mapQuery)}&zoom=15`}
                                title={provider.name}
                                width="100%"
                                height="400"
                                style={{ border: 0, display: 'block' }}
                                allowFullScreen
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                            />
                        </div>
                    </div>
                )}
                {/* Weboldalak - mindig a DOM-ban, csak display:none-nal elrejtve */}
                <div className={`d-flex flex-row gap-3 flex-wrap ${!isMapOpen ? 'd-none' : ''}`}>
                    {provider.contact?.web1 && (
                        <a 
                            href={provider.contact.web1.startsWith('http') ? provider.contact.web1 : `https://${provider.contact.web1}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="color-primary-600 text-md-medium text-decoration-none"
                        >
                            {provider.contact.web1}
                        </a>
                    )}
                    {provider.contact?.web2 && (
                        <a 
                            href={provider.contact.web2.startsWith('http') ? provider.contact.web2 : `https://${provider.contact.web2}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="color-primary-600 text-md-medium text-decoration-none"
                        >
                            {provider.contact.web2}
                        </a>
                    )}
                </div>
            </div>
        )}
    </div>
}

export function PublicProviderProfileContact({id, image, name, providerName, email, phone, extraText, openContact, is_preview}) {
    const [showPhone, setShowPhone] = useState(false);
    const { trackEvent } = useAnalyticsTracking('googleTagManager');

    const { mutate: sendClick } = useMutation({
        mutationFn: phoneClick,
    });

    const handleSendClick = () => {
        openContact();

        trackEvent('offer_request_open', {
            provider_id: id,
            provider_name: providerName,
            source: 'contact_section'
        });

        if (window.innerWidth > 902) {
            window.scroll({behavior: 'smooth', top: 0});
        }
    }

    const handlePhoneClick = () => {
        setShowPhone(true);
        sendClick({id: id});
    }

    return <div className="d-flex flex-column align-self-stretch">
        <p className="margin-0 color-gray-700 text-md-medium">Kontakt</p>
        <div className={`${classes["public-provider-contact-section"]}`}>
            <div className="d-flex flex-column align-self-stretch align-items-start gap-2">
                <div className="d-flex flex-row gap-3 w-100">
                    {image && <div className="flex-shrink-0">
                        <Avatar size={"sm"} profile url={image} />
                    </div>}
                    {name && <div className="d-flex flex-column align-self-stretch justify-content-center overflow-auto flex-grow-1">
                        <p className="margin-0 color-gray-700 text-md-semibold text-truncate">{name}</p>
                    </div>}
                </div>
                {/* Kpadminban megadható plusz szöveg (SOP-331) */}
                {extraText && <p className={`margin-0 color-gray-700 text-md-regular ${classes["public-provider-contact-extra-text"]}`}>{extraText}</p>}
            </div>
            <div className={`${classes["public-provider-contact-button-container"]}`}>
                <div className={`${classes["public-provider-contact-button-send-button"]}`}>
                    <CustomButton className="w-100" size="lg" hierarchy="primary" icon="leading" is_svg={true} svg={Send} onClick={handleSendClick} disabled={is_preview}>Kapcsolatfelvétel</CustomButton>
                </div>
                {phone && <>
                    {showPhone
                        ? <CustomButton size="lg" hierarchy="link-gray" icon="leading" is_svg={true} svg={PhoneCall} href={`tel:${phone}`} disabled={is_preview}>{phone}</CustomButton>
                        : <div className="d-flex flex-row align-items-center gap-2">
                            <CustomSVG className="flex-shrink-0 stroke-gray-500" SvgFunction={PhoneCall} height={20} width={20} />
                            <p className="margin-0 color-gray-500 text-md-semibold text-truncate overflow-none" style={{width: 45}}>{phone}</p>
                            <CustomButton size="lg" hierarchy="link-color" onClick={handlePhoneClick} disabled={is_preview}>Telefonszám felfedése</CustomButton>
                        </div>
                    }
                </>}
            </div>
        </div>
    </div>
}